import { useState, useEffect, useReducer } from "react";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

import api from "../../services/api";

const reducer = (state, action) => {
	if (action.type === "LOAD_WHATSAPPS") {
		const whatsApps = action.payload || [];

		return [...whatsApps];
	}

	if (action.type === "UPDATE_WHATSAPPS") {
		const whatsApp = action.payload;
		const whatsAppIndex = state.findIndex(s => s.id === whatsApp.id);

		if (whatsAppIndex !== -1) {
			state[whatsAppIndex] = whatsApp;
			return [...state];
		} else {
			return [whatsApp, ...state];
		}
	}

	if (action.type === "UPDATE_SESSION") {
		const whatsApp = action.payload;
		const whatsAppIndex = state.findIndex(s => s.id === whatsApp.id);

		if (whatsAppIndex !== -1) {
			state[whatsAppIndex].status = whatsApp.status;
			state[whatsAppIndex].updatedAt = whatsApp.updatedAt;
			state[whatsAppIndex].qrcode = whatsApp.qrcode;
			state[whatsAppIndex].retries = whatsApp.retries;
			return [...state];
		} else {
			return [...state];
		}
	}

	if (action.type === "DELETE_WHATSAPPS") {
		const whatsAppId = action.payload;

		const whatsAppIndex = state.findIndex(s => s.id === whatsAppId);
		if (whatsAppIndex !== -1) {
			state.splice(whatsAppIndex, 1);
		}
		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

const TRANSITIONAL_STATUSES = ["OPENING", "qrcode", "PAIRING"];
const STABLE_POLL_MS = 30_000;
const TRANSITIONAL_POLL_MS = 7_000;

const useWhatsApps = () => {
	const [whatsApps, dispatch] = useReducer(reducer, []);
	const [loading, setLoading] = useState(true);

	const fetchWhatsApps = async () => {
		try {
			const { data } = await api.get("/whatsapp/");
			dispatch({ type: "LOAD_WHATSAPPS", payload: data });
			setLoading(false);
		} catch (err) {
			setLoading(false);
			// 401/403 são tratados pelo interceptor do axios (refresh + retry)
			// exibir toast aqui causaria "Sessão expirada" falso no F5
			if (err?.response?.status !== 401 && err?.response?.status !== 403) {
				toastError(err);
			}
		}
	};

	useEffect(() => {
		setLoading(true);
		fetchWhatsApps();
	}, []);

	// Polling leve: garante que status visual se recupera após restart do backend
	// sem depender exclusivamente de socket events que podem ser perdidos na janela de reconexão.
	// Acelera enquanto alguma conexão está em transição (OPENING/qrcode/PAIRING) — é quando
	// o status muda rápido e o usuário está olhando a tela esperando o QR ou a conexão firmar.
	const hasTransitional = whatsApps.some(w => TRANSITIONAL_STATUSES.includes(w.status));
	useEffect(() => {
		const interval = setInterval(fetchWhatsApps, hasTransitional ? TRANSITIONAL_POLL_MS : STABLE_POLL_MS);
		return () => clearInterval(interval);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [hasTransitional]);

	useEffect(() => {
		const socket = openSocket();

		// Re-sync ao reconectar: eventos emitidos durante downtime do backend seriam perdidos
		socket.on("connect", fetchWhatsApps);

		socket.on("whatsapp", data => {
			if (data.action === "update") {
				dispatch({ type: "UPDATE_WHATSAPPS", payload: data.whatsapp });
			}
			if (data.action === "delete") {
				dispatch({ type: "DELETE_WHATSAPPS", payload: data.whatsappId });
			}
		});

		socket.on("whatsappSession", data => {
			if (data.action === "update") {
				dispatch({ type: "UPDATE_SESSION", payload: data.session });
			}
		});

		return () => {
			socket.off("connect", fetchWhatsApps);
			socket.off("whatsapp");
			socket.off("whatsappSession");
			socket.disconnect();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { whatsApps, loading, refresh: fetchWhatsApps };
};

export default useWhatsApps;
